import React, { useState } from 'react';
import { X } from 'lucide-react';

/** Filled 24×24 “document / lines” note — same visual language as LOS + distance icons on trip cards. */
function NoteGlyph({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-10 4h8v2H9V7zm0 4h8v2H9v-2zm0 4h5v2H9v-2z"
      />
    </svg>
  );
}

/** Resolve company note from Firebase / API shaped trip objects. */
export function getCompanyNote(trip) {
  if (!trip) return '';
  const n = trip.companyNote ?? trip.company_note;
  if (n == null || n === '') return '';
  const s = String(n).trim();
  return s;
}

/**
 * Driver notes: filled icon (aligned with card metadata), optional “Notes” label in grid rows.
 * @param {Object} props
 * @param {string} props.companyNote
 * @param {boolean} [props.compact] — icon only (badge row / tight spaces); no label
 */
export default function CompanyNoteIconButton({ companyNote, className = '', compact = false }) {
  const [open, setOpen] = useState(false);
  const text = companyNote != null ? String(companyNote).trim() : '';
  if (!text) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`
          group inline-flex min-w-0 max-w-full items-center gap-x-1.5 text-left
          m-0 border-0 bg-transparent p-0 font-inherit text-xs leading-tight
          text-main-light transition-colors
          hover:text-main-primary
          focus:outline-none focus-visible:ring-2 focus-visible:ring-main-primary/30 focus-visible:ring-offset-1
          ${className}
        `}
        title="Notes"
        aria-label="View notes"
      >
        <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center self-center" aria-hidden>
          <NoteGlyph className="h-4 w-4 text-current" />
        </span>
        {!compact && (
          <span className="min-w-0 max-w-full truncate text-main-light group-hover:text-main-primary">
            Notes
          </span>
        )}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-[2200] bg-black/40 backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-hidden="true"
          />
          <div className="pointer-events-none fixed inset-0 z-[2201] flex items-center justify-center p-4">
            <div
              className="pointer-events-auto w-full max-w-md rounded-xl border border-main-base/20 bg-main-base-50 p-4 text-left shadow-xl ring-1 ring-main-base"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="notes-dialog-title"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <h3
                  id="notes-dialog-title"
                  className="flex items-center gap-2 text-sm font-semibold text-main-dark"
                >
                  <span className="inline-flex h-4 w-4 text-main-primary">
                    <NoteGlyph className="h-4 w-4" />
                  </span>
                  Notes
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-md p-1 text-main-light hover:bg-white/80 hover:text-main-dark"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-main-secondary">
                {text}
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
